import { useState, useCallback, useRef } from 'react';
import { type UploadFile, type UploadToken, type UploadStartResponse } from '../lib/uploadTypes';
import { validateFile } from '../lib/fileValidation';
import { uploadFile as gcsUploadFile, type UploadProgress } from '../lib/gcsUpload';

interface UseUploadManagerOptions {
  token: UploadToken;
  tokenString: string;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

export function useUploadManager({
  token,
  tokenString,
  onComplete,
  onError,
}: UseUploadManagerOptions) {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [remainingQuota, setRemainingQuota] = useState<number>(token.limits.maxTotalBytes);
  const [notes, setNotes] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [allComplete, setAllComplete] = useState(false);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  /**
   * Generate unique ID for a file
   */
  const generateFileId = useCallback((): string => {
    return `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  /**
   * Add files to the upload queue
   */
  const addFiles = useCallback(
    (newFiles: File[]) => {
      const validatedFiles: UploadFile[] = [];

      for (const file of newFiles) {
        const validation = validateFile(file, token.limits.maxFileBytes);

        if (!validation.valid) {
          // Add file with error status
          validatedFiles.push({
            id: generateFileId(),
            file,
            progress: 0,
            status: 'error',
            error: validation.error,
            retryCount: 0,
          });
          continue;
        }

        // Check total quota
        const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);
        if (totalSize + file.size > remainingQuota) {
          validatedFiles.push({
            id: generateFileId(),
            file,
            progress: 0,
            status: 'error',
            error: `File would exceed remaining quota (${(remainingQuota / (1024 * 1024 * 1024)).toFixed(2)} GB remaining)`,
            retryCount: 0,
          });
          continue;
        }

        validatedFiles.push({
          id: generateFileId(),
          file,
          progress: 0,
          status: 'pending',
          error: undefined,
          retryCount: 0,
        });
      }

      setFiles(prev => [...prev, ...validatedFiles]);
    },
    [files, remainingQuota, token.limits.maxFileBytes, generateFileId]
  );

  /**
   * Remove file from queue
   */
  const removeFile = useCallback((fileId: string) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file?.status === 'uploading') {
        // Cancel upload
        const controller = abortControllersRef.current.get(fileId);
        if (controller) {
          controller.abort();
          abortControllersRef.current.delete(fileId);
        }
      }
      return prev.filter(f => f.id !== fileId);
    });
  }, []);

  /**
   * Retry failed upload
   */
  const retryFile = useCallback((fileId: string) => {
    setFiles(prev =>
      prev.map(f =>
        f.id === fileId
          ? {
              ...f,
              status: 'pending',
              error: undefined,
              retryCount: f.retryCount + 1,
            }
          : f
      )
    );
  }, []);

  /**
   * Call uploadStart API to get session URL
   */
  const getUploadSession = async (
    file: File,
    tokenString: string
  ): Promise<UploadStartResponse> => {
    try {
      const response = await fetch('/.netlify/functions/uploadStart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: tokenString,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw new Error(
        `Failed to start upload session: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  /**
   * Upload a single file
   */
  const uploadSingleFile = async (uploadFile: UploadFile, tokenString: string): Promise<void> => {
    // Update status to validating
    setFiles(prev => prev.map(f => (f.id === uploadFile.id ? { ...f, status: 'validating' } : f)));

    try {
      // Get upload session from API
      const sessionResponse = await getUploadSession(uploadFile.file, tokenString);

      if (!sessionResponse.success || (!sessionResponse.sessionUrl && !sessionResponse.signedUrl)) {
        throw new Error(sessionResponse.error || 'Failed to create upload session');
      }

      // Update with session/signed URL and remaining quota
      setFiles(prev =>
        prev.map(f =>
          f.id === uploadFile.id
            ? {
                ...f,
                sessionUrl: sessionResponse.sessionUrl,
                signedUrl: sessionResponse.signedUrl,
                uploadMethod:
                  sessionResponse.uploadMethod ||
                  (sessionResponse.signedUrl ? 'signed' : 'resumable'),
                status: 'uploading',
              }
            : f
        )
      );

      if (sessionResponse.remainingQuota !== undefined) {
        setRemainingQuota(sessionResponse.remainingQuota);
      }

      // Create abort controller for this upload
      const controller = new AbortController();
      abortControllersRef.current.set(uploadFile.id, controller);

      // Upload to GCS (using signed URL if available, otherwise resumable)
      const result = await gcsUploadFile({
        sessionUrl: sessionResponse.sessionUrl,
        signedUrl: sessionResponse.signedUrl,
        uploadMethod:
          sessionResponse.uploadMethod || (sessionResponse.signedUrl ? 'signed' : 'resumable'),
        file: uploadFile.file,
        onProgress: (progress: UploadProgress) => {
          setFiles(prev =>
            prev.map(f =>
              f.id === uploadFile.id
                ? {
                    ...f,
                    progress: progress.percentage,
                    uploadedBytes: progress.loaded,
                  }
                : f
            )
          );
        },
      });

      // Remove abort controller
      abortControllersRef.current.delete(uploadFile.id);

      if (result.success) {
        setFiles(prev =>
          prev.map(f => (f.id === uploadFile.id ? { ...f, status: 'complete', progress: 100 } : f))
        );
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      // Check if aborted
      const controller = abortControllersRef.current.get(uploadFile.id);
      if (controller?.signal.aborted) {
        setFiles(prev =>
          prev.map(f => (f.id === uploadFile.id ? { ...f, status: 'cancelled' } : f))
        );
        return;
      }

      setFiles(prev =>
        prev.map(f =>
          f.id === uploadFile.id
            ? {
                ...f,
                status: 'error',
                error: error instanceof Error ? error.message : 'Upload failed',
              }
            : f
        )
      );

      if (onError) {
        onError(error instanceof Error ? error.message : 'Upload failed');
      }
    }
  };

  /**
   * Start uploading all pending files
   */
  const startUpload = useCallback(async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) {
      return;
    }

    setIsUploading(true);
    setAllComplete(false);

    // Upload files sequentially to avoid overwhelming the API
    for (const file of pendingFiles) {
      await uploadSingleFile(file, tokenString);
    }

    setIsUploading(false);

    // Check if all files are complete
    const allFilesComplete = files.every(
      f => f.status === 'complete' || f.status === 'error' || f.status === 'cancelled'
    );
    if (allFilesComplete) {
      setAllComplete(true);
      if (onComplete) {
        onComplete();
      }
    }
  }, [files, onComplete, tokenString]);

  /**
   * Cancel all uploads
   */
  const cancelAll = useCallback(() => {
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current.clear();
    setFiles(prev => prev.map(f => (f.status === 'uploading' ? { ...f, status: 'cancelled' } : f)));
    setIsUploading(false);
  }, []);

  /**
   * Reset upload state
   */
  const reset = useCallback(() => {
    cancelAll();
    setFiles([]);
    setNotes('');
    setAllComplete(false);
    setRemainingQuota(token.limits.maxTotalBytes);
  }, [cancelAll, token.limits.maxTotalBytes]);

  const totalProgress =
    files.length > 0 ? files.reduce((sum, f) => sum + f.progress, 0) / files.length : 0;

  const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);
  const uploadedSize = files.reduce((sum, f) => sum + (f.uploadedBytes || 0), 0);

  return {
    files,
    addFiles,
    removeFile,
    retryFile,
    startUpload,
    cancelAll,
    reset,
    notes,
    setNotes,
    isUploading,
    allComplete,
    remainingQuota,
    totalProgress,
    totalSize,
    uploadedSize,
  };
}
